import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowPathIcon, ExclamationTriangleIcon } from './icons';

interface ConnectionStatusScreenProps {
    /** connecting: Profil wird erneut geladen; failed: alle Versuche gescheitert */
    variant: 'connecting' | 'failed';
    onRetry?: () => void;
    isRetrying?: boolean;
    onLogout?: () => void;
}

/**
 * Langsame oder unterbrochene Verbindung beim Laden des eigenen Profils (AuthContext). Ersetzt die
 * irreführende Meldung "Keine Organisation zugewiesen", wenn nur das Laden gescheitert ist.
 */
export const ConnectionStatusScreen: React.FC<ConnectionStatusScreenProps> = ({ variant, onRetry, isRetrying, onLogout }) => {
    const { t } = useTranslation();

    if (variant === 'connecting') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-gray-50 p-4 text-center" role="status">
                <div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-gray-500 rounded-full" aria-hidden="true"></div>
                <p className="font-medium text-gray-800">{t('app.connection.connectingTitle', 'Verbindung wird hergestellt …')}</p>
                <p className="max-w-sm text-sm text-gray-600">{t('app.connection.connectingMessage', 'Die Verbindung ist langsam. Bitte einen Moment Geduld.')}</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4 text-center">
            <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md" role="alert">
                <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ExclamationTriangleIcon className="w-7 h-7 text-amber-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">{t('app.connection.failedTitle', 'Verbindung fehlgeschlagen')}</h2>
                <p className="text-gray-600 mb-6">
                    {t('app.connection.failedMessage', 'Ihre Anmeldung ist in Ordnung, aber Ihre Daten konnten nicht geladen werden, meist wegen einer langsamen oder unterbrochenen Internetverbindung. Wir versuchen es automatisch weiter.')}
                </p>
                <div className="flex flex-col gap-3">
                    {onRetry && (
                        <button
                            type="button"
                            onClick={onRetry}
                            disabled={isRetrying}
                            className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-60"
                        >
                            <ArrowPathIcon className={isRetrying ? 'w-4 h-4 animate-spin' : 'w-4 h-4'} />
                            {isRetrying ? t('app.connection.retrying', 'Wird versucht …') : t('app.connection.retry', 'Erneut versuchen')}
                        </button>
                    )}
                    {onLogout && (
                        <button
                            type="button"
                            onClick={onLogout}
                            className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-gray-400"
                        >
                            {t('sidebar.logout')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
